import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { Grid } from "@mui/material"
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"

import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { Order } from "../../../../../merchant/controlKitchenTypes"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { TableToolbarBanner } from "../../../../../merchant/merchant/production/components/TableToolbarBanner"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { TableSummaryWrap, TableTotalAmountBox } from "../../../../../../shared/components/table/TableSummaryBox"
import FloatingOrderActionBar from "../../../../../merchant/merchant/production/components/FloatingOrderActionBar"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectUserId } from "../../../../../login/loginSelectors"
import {
    ArchivePOSTransactions,
    fetchPOSTransactions,
    FetchPOSTransactionsResponse,
} from "../../userDailyPurchases/userPOSTransactionsApi"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const selectOrders = (data: FetchPOSTransactionsResponse) => data?.orders || []

export function UserRefillWallet() {
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)

    const [selectedRows, setSelectedRows] = useState<Order[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<Order>>(null)
    const [receipt, setReceipt] = useState<Receipt>()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [showNotPrintedOrders, setShowNotPrintedOrders] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const orderList = useApiQuery(fetchPOSTransactions, {
        queryName: ArchivePOSTransactions.ARCHIVE_WALLET_REFILL_TRANSACTIONS,
        dependencies: {
            userId: userId ?? 0,
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
        },
        select: selectOrders,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(orderList)

    const columns = useMemo<MRT_ColumnDef<Order>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-archive-transactions-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("user-archive-transactions-table-transaction-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.moneyColumn(
                { header: l10n.getString("office-purchase-amount"), id: "amount" },
                (row) => row.amount,
                true,
            ),
        ],
        [columnFactory],
    )

    function showReceipt(row: Order) {
        setReceipt({
            orderUid: row.uid,
            orderId: row.id,
            orderType: row.orderType,
        })
    }

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const hasNonePrinted = () => {
        if (!orderList.data) {
            return
        }
        return (
            orderList.data.filter((order) => {
                return order.printDetails?.isPrinted === false
            }).length > 0
        )
    }

    const showAlertBanner = useMemo(() => hasNonePrinted(), [orderList.data])

    const handleClearSelected = useCallback(() => {
        setSelectedRows(undefined)
        setRowSelection({})
    }, [])

    return (
        <>
            <Grid container direction="column" wrap="nowrap">
                {showAlertBanner && (
                    <TableToolbarBanner
                        showNotPrintedOrders={showNotPrintedOrders}
                        setShowNotPrintedOrders={setShowNotPrintedOrders}
                    />
                )}

                <ApiQueryTable
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onColumnFiltersChange={setColumnFilters}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap padding="sm">
                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                    state={{
                        columnFilters,
                        rowSelection,
                    }}
                />
            </Grid>

            {selectedRows && (
                <FloatingOrderActionBar
                    selectedRows={selectedRows}
                    startDate={dateState.startDate ?? DateTime.now()}
                    endDate={dateState.endDate ?? DateTime.now()}
                    handleClearSelected={handleClearSelected}
                />
            )}

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={orderList.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
